import React, { useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { FiPhone, FiX } from 'react-icons/fi';

function getName(user) {
  if (!user) return 'Сотрудник';
  const n = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return n || user.username || `ID ${user.id}`;
}

export default function VoiceCallOverlay() {
  const { state, dispatch } = useApp();
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const signalOffRef = useRef(null);

  const voiceCall = state.voiceCall;
  const otherUser = voiceCall?.toUser || voiceCall?.fromUser;
  const otherId = otherUser?.id;

  const cleanup = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }
    if (signalOffRef.current) {
      signalOffRef.current();
      signalOffRef.current = null;
    }
  }, []);

  const sendSignal = useCallback((signal) => {
    if (!window.socket || !otherId) return;
    window.socket.emit('voice_call:signal', { toUserId: otherId, signal });
  }, [otherId]);

  const endCall = useCallback(() => {
    if (window.socket && otherId) {
      window.socket.emit('voice_call:end', { toUserId: otherId });
    }
    cleanup();
    dispatch({ type: 'CLEAR_VOICE_CALL' });
  }, [otherId, cleanup, dispatch]);

  const handleReject = useCallback(() => {
    if (window.socket && voiceCall?.fromUser?.id) {
      window.socket.emit('voice_call:reject', { toUserId: voiceCall.fromUser.id });
    }
    dispatch({ type: 'CLEAR_VOICE_CALL', payload: voiceCall?.fromUser?.id != null ? { fromUserId: voiceCall.fromUser.id } : undefined });
  }, [voiceCall?.fromUser?.id, dispatch]);

  const handleCancel = useCallback(() => {
    if (window.socket && otherId) {
      window.socket.emit('voice_call:end', { toUserId: otherId });
    }
    dispatch({ type: 'CLEAR_VOICE_CALL' });
  }, [otherId, dispatch]);

  const handleAccept = useCallback(async () => {
    if (!voiceCall?.fromUser?.id || !window.socket) return;
    const fromId = voiceCall.fromUser.id;
    let stream;
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('NOT_SUPPORTED');
      }
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('Accept call error:', err);
      const isNotFound = err?.name === 'NotFoundError' || err?.message?.includes('Requested device not found');
      const isNotAllowed = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      const msg = isNotFound
        ? 'Микрофон не найден. Подключите микрофон и проверьте настройки браузера.'
        : isNotAllowed
          ? 'Доступ к микрофону запрещён. Разрешите использование микрофона в настройках сайта.'
          : err?.name === 'NOT_SUPPORTED'
            ? 'Голосовые звонки не поддерживаются в этом браузере.'
            : 'Не удалось получить доступ к микрофону.';
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: msg } });
      handleReject();
      return;
    }
    try {
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      peerRef.current = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.ontrack = (e) => {
        if (remoteAudioRef.current && e.streams[0]) {
          remoteAudioRef.current.srcObject = e.streams[0];
        }
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) sendSignal({ type: 'ice', candidate: e.candidate });
      };

      const onSignal = ({ fromUserId, signal }) => {
        if (Number(fromUserId) !== Number(fromId)) return;
        if (signal.type === 'offer') {
          pc.setRemoteDescription(new RTCSessionDescription(signal)).then(() => pc.createAnswer())
            .then(answer => pc.setLocalDescription(answer))
            .then(() => sendSignal({ type: 'answer', sdp: pc.localDescription.sdp }));
        } else if (signal.type === 'ice' && signal.candidate) {
          pc.addIceCandidate(new RTCIceCandidate(signal.candidate)).catch(() => {});
        }
      };
      window.socket.on('voice_call:signal', onSignal);
      signalOffRef.current = () => window.socket.off('voice_call:signal', onSignal);

      dispatch({ type: 'SET_VOICE_CALL', payload: { status: 'active', fromUser: null, toUser: voiceCall.fromUser, isCaller: false } });
      window.socket.emit('voice_call:accept', { toUserId: fromId });
    } catch (err) {
      console.error('Accept call setup error:', err);
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
      }
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Ошибка при установке звонка' } });
      handleReject();
    }
  }, [voiceCall?.fromUser, dispatch, sendSignal, handleReject]);

  useEffect(() => {
    if (!voiceCall || voiceCall.status !== 'active' || !otherId || !window.socket || !voiceCall.isCaller) return;
    if (peerRef.current) return;

    let pc;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });
        peerRef.current = pc;
        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        pc.ontrack = (e) => {
          if (remoteAudioRef.current && e.streams[0]) {
            remoteAudioRef.current.srcObject = e.streams[0];
          }
        };
        pc.onicecandidate = (e) => {
          if (e.candidate) sendSignal({ type: 'ice', candidate: e.candidate });
        };

        const onSignal = ({ fromUserId, signal }) => {
          if (Number(fromUserId) !== Number(otherId)) return;
          if (signal.type === 'answer') {
            pc.setRemoteDescription(new RTCSessionDescription(signal)).catch(() => {});
          } else if (signal.type === 'ice' && signal.candidate) {
            pc.addIceCandidate(new RTCIceCandidate(signal.candidate)).catch(() => {});
          }
        };
        window.socket.on('voice_call:signal', onSignal);
        signalOffRef.current = () => window.socket.off('voice_call:signal', onSignal);

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSignal({ type: 'offer', sdp: offer.sdp });
      } catch (err) {
        console.error('Start call error:', err);
        const isNotFound = err?.name === 'NotFoundError' || err?.message?.includes('Requested device not found');
        const isNotAllowed = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
        const msg = isNotFound
          ? 'Микрофон не найден. Подключите микрофон и проверьте настройки браузера.'
          : isNotAllowed
            ? 'Доступ к микрофону запрещён. Разрешите использование микрофона в настройках сайта.'
            : 'Не удалось получить доступ к микрофону.';
        dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: msg } });
        endCall();
      }
    })();
  }, [voiceCall?.status, voiceCall?.isCaller, otherId, sendSignal, endCall, dispatch]);

  useEffect(() => {
    if (!voiceCall) return;
    const onEnded = () => {
      cleanup();
      dispatch({ type: 'CLEAR_VOICE_CALL' });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'info', message: 'Звонок завершён' } });
    };
    window.socket?.on('voice_call:ended', onEnded);
    return () => { window.socket?.off('voice_call:ended', onEnded); };
  }, [voiceCall, cleanup, dispatch]);

  useEffect(() => () => cleanup(), [cleanup]);

  if (!voiceCall) return null;

  const name = getName(otherUser);
  const isIncoming = voiceCall.status === 'incoming';
  const isOutgoing = voiceCall.status === 'outgoing';
  const isActive = voiceCall.status === 'active';

  return (
    <>
      <audio ref={remoteAudioRef} autoPlay playsInline />
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 200010,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            borderRadius: 20,
            padding: 32,
            minWidth: 280,
            maxWidth: 360,
            textAlign: 'center',
            border: '1px solid rgba(67, 233, 123, 0.3)',
            boxShadow: '0 0 40px rgba(67, 233, 123, 0.2)'
          }}
        >
          <div style={{ color: '#43e97b', marginBottom: 12 }}>
            <FiPhone size={48} />
          </div>
          <div style={{ color: '#e2e8f0', fontSize: '1.2rem', fontWeight: 600, marginBottom: 8 }}>
            {isIncoming && `Входящий от ${name}`}
            {isOutgoing && `Звонок ${name}...`}
            {isActive && `Разговор с ${name}`}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
            {isIncoming && (
              <>
                <button onClick={handleReject} style={btnReject}>
                  <FiX size={20} /> Отклонить
                </button>
                <button onClick={handleAccept} style={btnAccept}>
                  <FiPhone size={20} /> Принять
                </button>
              </>
            )}
            {isOutgoing && (
              <button onClick={handleCancel} style={btnReject}>
                <FiX size={20} /> Отмена
              </button>
            )}
            {isActive && (
              <button onClick={endCall} style={btnReject}>
                <FiPhone size={20} style={{ transform: 'rotate(135deg)' }} /> Завершить
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const btnReject = {
  padding: '12px 24px',
  borderRadius: 12,
  border: '1px solid rgba(239, 68, 68, 0.6)',
  background: 'rgba(239, 68, 68, 0.2)',
  color: '#fca5a5',
  cursor: 'pointer',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8
};
const btnAccept = {
  padding: '12px 24px',
  borderRadius: 12,
  border: '1px solid rgba(67, 233, 123, 0.6)',
  background: 'rgba(67, 233, 123, 0.2)',
  color: '#86efac',
  cursor: 'pointer',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8
};
